/* eslint-disable operator-linebreak */
import { ReactElement, useState, useEffect } from 'react';
import { tile, iFVSInfoDisplay } from '@interfaces/interfaces';
import { Field, useFormikContext, getIn } from 'formik';
import gridStyles from '@pages/forms/Grid.module.scss';
import { TextInput, DateInput, LicenceField, AddressField, CheckboxInput, TileRadio, EmailFieldWithExperian, ContactInputWithExperian, LimitedTextArea } from '@components';
import { iFVSFormValues } from '@services/FVSService';
import styles from './VictimInformation.module.scss';

interface iVictimInformation {
  displayObject: iFVSInfoDisplay;
  currentStepNo: string;
}

function VictimInformation({ displayObject, currentStepNo }: iVictimInformation): ReactElement {
  const formikContext = useFormikContext();
  const [isDisabled, setDisabled] = useState(false);
  const [isPostalDisabled, setPostalDisabled] = useState(false);
  const { values }: { values: iFVSFormValues } = useFormikContext();
  const { pageTitle, pageSubtitle } = displayObject.pages.contactInformation[currentStepNo];
  const {
    givenName,
    familyName,
    dateOfBirth,
    driverLicence,
    residentialAddress,
    noFixedAddress,
    noResidentialAddress,
    deliveryAddressType,
    postalAddress,
    noPostalAddress,
    isEmailRequired,
    noEmail,
    email,
    sendEmailCopy,
    phoneApplicant,
    individualContactNumber,
    safestContactTime,
    phoneMessagePrompt,
    interpreterRequiredPrompt,
    alternativeLanguage,
  } = displayObject.pages.contactInformation[currentStepNo].fields;
  const { FVSPostalAddress, FVSResidentialAddress } = values;
  const isPostalAddressEmpty = FVSPostalAddress.LineOne === '' && FVSPostalAddress.Postcode === '' && FVSPostalAddress.State === '' && FVSPostalAddress.Suburb === '';
  const isResidentialAddressEmpty =
    FVSResidentialAddress.LineOne === '' && FVSResidentialAddress.Postcode === '' && FVSResidentialAddress.State === '' && FVSResidentialAddress.Suburb === '';
  const YesValue = (): ReactElement => (
    <>
      <div>Yes</div>
    </>
  );

  const NoValue = (): ReactElement => (
    <>
      <div>No</div>
    </>
  );

  const TILE_DATA: tile[] = [
    {
      body: <YesValue />,
      value: 'true',
    },
    {
      body: <NoValue />,
      value: 'false',
    },
  ];
  const RepresentativeAddressPreferred = (): ReactElement => (
    <>
      <div>Authorised representative's postal address</div>
    </>
  );
  const ResidentialAddressPreferred = (): ReactElement => {
    if (currentStepNo === 'step6') {
      return (
        <>
          <div>Residential address</div>
        </>
      );
    }
    return (
      <>
        <div>Fineholder's residential address</div>
      </>
    );
  };
  const OtherPostalAddressPreferred = (): ReactElement => (
    <>
      <div>
        Another postal address
        <br />
        This could be a PO box or address of a friend, relative
      </div>
    </>
  );
  const EmailPreferred = (): ReactElement => (
    <>
      <div>I do not have a safe postal address, please email me</div>
    </>
  );

  const PreferredDeliveryAddressType: tile[] = [
    currentStepNo === 'step6'
      ? {
          body: <ResidentialAddressPreferred />,
          value: 'ResidentialAddress',
        }
      : {
          body: <RepresentativeAddressPreferred />,
          value: 'RepresentativeAddress',
        },
    currentStepNo === 'step6'
      ? {
          body: <OtherPostalAddressPreferred />,
          value: 'PostalAddress',
        }
      : {
          body: <ResidentialAddressPreferred />,
          value: 'ResidentialAddress',
        },
    currentStepNo === 'step6'
      ? {
          body: <EmailPreferred />,
          value: 'EmailAddress',
        }
      : {
          body: <OtherPostalAddressPreferred />,
          value: 'PostalAddress',
        },
  ];
  useEffect(() => {
    /* General conditions */
    if (getIn(values, noFixedAddress.formikName) || getIn(values, noResidentialAddress.formikName)) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
    if (getIn(values, noPostalAddress.formikName) || getIn(values, noPostalAddress.formikName)) {
      setPostalDisabled(true);
    } else {
      setPostalDisabled(false);
    }
    /* Step6 conditions */
    if (currentStepNo === 'step6' && (getIn(values, noFixedAddress.formikName) || getIn(values, noResidentialAddress.formikName)) && getIn(values, noPostalAddress.formikName)) {
      formikContext.setFieldTouched(postalAddress.formikName);
    }
    if (currentStepNo === 'step6' && getIn(values, deliveryAddressType.formikName) === 'PostalAddress') {
      formikContext.setFieldValue(noPostalAddress.formikName, false);
      formikContext.setFieldTouched(noPostalAddress.formikName);
    }
    if (currentStepNo === 'step6' && getIn(values, noPostalAddress.formikName) && getIn(formikContext.values, deliveryAddressType.formikName) === 'PostalAddress') {
      formikContext.setFieldValue(deliveryAddressType.formikName, '');
      formikContext.setFieldTouched(deliveryAddressType.formikName);
    }
    /* Step15 conditions */
    if (
      currentStepNo === 'step15' &&
      (getIn(formikContext.values, noResidentialAddress.formikName) || getIn(formikContext.values, noFixedAddress.formikName)) &&
      getIn(formikContext.values, deliveryAddressType.formikName) === 'ResidentialAddress'
    ) {
      formikContext.setFieldValue(deliveryAddressType.formikName, '');
      formikContext.setFieldTouched(deliveryAddressType.formikName);
    }
  }, [
    getIn(values, noFixedAddress.formikName),
    getIn(values, noResidentialAddress.formikName),
    getIn(values, noPostalAddress.formikName),
    getIn(values, deliveryAddressType.formikName),
    currentStepNo,
  ]);
  return (
    <>
      <div className="vfH2">{pageTitle}</div>
      <div className="subHeadingH3">{pageSubtitle}</div>
      <div className={styles.subHeadingInfo}>All fields are required unless specified otherwise.</div>
      <div className={gridStyles.form}>
        <div className={gridStyles.gridColFull}>
          <Field component={TextInput} name={givenName.formikName} title={givenName.title} isMandatory />
        </div>
        <div className={gridStyles.gridColFull}>
          <Field component={TextInput} name={familyName.formikName} title={familyName.title} isMandatory />
        </div>
        <div className={gridStyles.gridColHalf}>
          <Field name={dateOfBirth.formikName} component={DateInput} minDate={new Date(1900, 1, 1)} title={dateOfBirth.title} isMandatory maxDate={new Date()} />
        </div>
        <div className={gridStyles.gridColFull}>
          <LicenceField
            title={driverLicence.title}
            name={driverLicence.formikName}
            nameStateOfIssue="LicenceState"
            nameCountryOfIssue="StatecountryOfIssue"
            nameNoDriverLicence="NoDriverLicence"
            noDriverLicenceTitle="Select if you do not hold a current driver licence"
            isMandatory
          />
        </div>
        <div className={gridStyles.gridColFull}>
          <AddressField
            name={residentialAddress.formikName}
            title={residentialAddress.title}
            manualAddressText="Enter address manually"
            movedRecentlyText={currentStepNo === 'step6' ? 'Have you moved recently?' : ''}
            isMandatory
            disabled={isDisabled}
          />
          <Field
            component={CheckboxInput}
            name={noFixedAddress.formikName}
            title={noFixedAddress.title}
            disabled={getIn(values, noResidentialAddress.formikName)}
            boxChecked={() => {
              formikContext.setFieldValue(residentialAddress.formikName, {
                SearchText: '',
                Address: '',
                LineOne: '',
                LineTwo: '',
                Suburb: '',
                State: '',
                Postcode: '',
                Country: 'Australia',
              });
              formikContext.setFieldTouched(residentialAddress.formikName);
            }}
          />
          <Field
            component={CheckboxInput}
            name={noResidentialAddress.formikName}
            title={noResidentialAddress.title}
            disabled={getIn(values, noFixedAddress.formikName)}
            boxChecked={() => {
              formikContext.setFieldValue(residentialAddress.formikName, {
                SearchText: '',
                Address: '',
                LineOne: '',
                LineTwo: '',
                Suburb: '',
                State: '',
                Postcode: '',
                Country: 'Australia',
              });
              formikContext.setFieldTouched(residentialAddress.formikName);
            }}
          />
        </div>
        {/* Delivery Address Type */}
        {currentStepNo === 'step6' && !getIn(values, noFixedAddress.formikName) && !getIn(values, noResidentialAddress.formikName) && (
          <div className={gridStyles.gridColFull}>
            <Field
              component={TileRadio}
              name={deliveryAddressType.formikName}
              title={deliveryAddressType.title}
              tileData={PreferredDeliveryAddressType}
              tileContainer
              isMandatory
              classname={styles.tile}
            />
          </div>
        )}
        {currentStepNo === 'step15' && (
          <div className={gridStyles.gridColFull}>
            <Field
              component={TileRadio}
              name={deliveryAddressType.formikName}
              title={deliveryAddressType.title}
              tileData={
                !getIn(values, noResidentialAddress.formikName) && !getIn(values, noFixedAddress.formikName)
                  ? PreferredDeliveryAddressType
                  : PreferredDeliveryAddressType.filter((type) => type.value !== 'ResidentialAddress')
              }
              tileContainer
              isMandatory
              classname={styles.tile}
            />
          </div>
        )}
        {/* Postal Address */}
        {currentStepNo === 'step6' &&
          (getIn(values, noFixedAddress.formikName) || getIn(values, noResidentialAddress.formikName) || getIn(values, deliveryAddressType.formikName) === 'PostalAddress') && (
            <div className={gridStyles.gridColFull}>
              <AddressField name={postalAddress.formikName} title={postalAddress.title} manualAddressText="Enter address manually" isMandatory disabled={isPostalDisabled} />
              {(getIn(values, noFixedAddress.formikName) || getIn(values, noResidentialAddress.formikName)) && (
                <Field
                  component={CheckboxInput}
                  name={noPostalAddress.formikName}
                  title={noPostalAddress.title}
                  boxChecked={() => {
                  formikContext.setFieldValue(postalAddress.formikName, {
                    SearchText: '',
                    Address: '',
                    LineOne: '',
                    LineTwo: '',
                    Suburb: '',
                    State: '',
                    Postcode: '',
                    Country: 'Australia',
                  });
                  formikContext.setFieldTouched(postalAddress.formikName);
                }} />
              )}
            </div>
          )}
        {currentStepNo === 'step15' && getIn(values, deliveryAddressType.formikName) === 'PostalAddress' && (
          <div className={gridStyles.gridColFull}>
            <AddressField name={postalAddress.formikName} title={postalAddress.title} manualAddressText="Enter address manually" isMandatory />
          </div>
        )}
        {/* Safe to email */}
        {currentStepNo === 'step6' &&
          (getIn(values, deliveryAddressType.formikName) === 'ResidentialAddress' ||
            getIn(values, deliveryAddressType.formikName) === 'PostalAddress' ||
            getIn(values, noFixedAddress.formikName) ||
            getIn(values, noResidentialAddress.formikName)) &&
          (!isResidentialAddressEmpty || !isPostalAddressEmpty) && (
            <div className={gridStyles.gridColFull}>
              <Field component={TileRadio} name={isEmailRequired.formikName} title={isEmailRequired.title} tileData={TILE_DATA} tileContainer isMandatory orientationRow />
            </div>
          )}
        {currentStepNo === 'step15' && (
          <div className={gridStyles.gridColFull}>
            <Field component={TileRadio} name={isEmailRequired.formikName} title={isEmailRequired.title} tileData={TILE_DATA} tileContainer isMandatory orientationRow />
          </div>
        )}
        {/* Email communication */}
        {currentStepNo === 'step6' &&
          ((isResidentialAddressEmpty && isPostalAddressEmpty) ||
            getIn(values, deliveryAddressType.formikName) === 'EmailAddress' ||
            getIn(values, isEmailRequired.formikName) === 'true') && (
            <div className={gridStyles.gridColFull}>
              <Field component={EmailFieldWithExperian} name={email.formikName} title="Email" isMandatory orientationRow obligationNumber={values.OBLIGATION_NUMBER} formName={displayObject.mode} noEmailField={noEmail.formikName} currentSP={noEmail.title} noEmailFieldRequired={false} warningMessage="EmailWarning" />
              <Field component={TileRadio} name={sendEmailCopy.formikName} title={sendEmailCopy.title} tileData={TILE_DATA} tileContainer isMandatory orientationRow />
            </div>
          )}
        {currentStepNo === 'step15' && getIn(values, isEmailRequired.formikName) === 'true' && (
          <div className={gridStyles.gridColFull}>
            <Field component={EmailFieldWithExperian} name={email.formikName} title="Email" isMandatory orientationRow obligationNumber={values.OBLIGATION_NUMBER} formName={displayObject.mode} noEmailField={noEmail.formikName} currentSP={noEmail.title} noEmailFieldRequired={false} warningMessage="EmailWarning" />
          </div>
        )}
        <div className={gridStyles.gridColFull}>
          <Field component={TileRadio} name={phoneApplicant.formikName} title={phoneApplicant.title} tileData={TILE_DATA} tileContainer isMandatory orientationRow />
        </div>
        {/* Phone communication */}
        {getIn(values, phoneApplicant.formikName) === 'true' && (
          <div className={gridStyles.gridColFull}>
            <Field component={ContactInputWithExperian} name={individualContactNumber.formikName} title="Contact phone number" isMandatory orientationRow obligationNumber={values.OBLIGATION_NUMBER} formName={displayObject.mode} secondaryContact={false} hasPrimaryNumberType={false} warningMessage="IndividualContactNumberWarning" />
            <Field component={LimitedTextArea} name={safestContactTime.formikName} title={safestContactTime.title} limit={400} isMandatory />
            <Field component={TileRadio} name={phoneMessagePrompt.formikName} title={phoneMessagePrompt.title} tileData={TILE_DATA} tileContainer isMandatory orientationRow />
            <Field
              component={TileRadio}
              name={interpreterRequiredPrompt.formikName}
              title={interpreterRequiredPrompt.title}
              tileData={TILE_DATA}
              tileContainer
              isMandatory
              orientationRow
            />
          </div>
        )}
        {getIn(values, phoneApplicant.formikName) === 'true' && getIn(values, interpreterRequiredPrompt.formikName) === 'true' && (
          <div className={gridStyles.gridColFull}>
            <Field component={TextInput} name={alternativeLanguage.formikName} title={alternativeLanguage.title} isMandatory />
          </div>
        )}
      </div>
    </>
  );
}
export default VictimInformation;
